import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { InvalidSpinError } from './errors/InvalidSpinError';
import { MissingScopeSpinError } from './errors/MissingScopeSpinError';

export interface ISpinStateResponse {
    readonly spinState: string;
    readonly spinLockedWaitingTime: number;
    readonly remainingTries: number;
}

export interface ISpinChangeBody {
    readonly existingSPin: string;
    readonly newSPin: string;
}

export interface ISpinBody {
    readonly spin: string;
}

export interface ISpinResetBody {
    readonly newSPin: string;
}

export class TwoFactorManagementClient {

    private readonly resourceHostId: string;

    private getGdcType = (isWcar: boolean) => isWcar ? `myvw-wcar-${this.stage}` : `myvw-mbb-${this.stage}`;

    constructor(private readonly authService: IAuthServiceV2, private readonly stage: string) {
        this.resourceHostId = `myvw-2fm-${stage}`;
    }

    public async getSpinState(isWcar: boolean): Promise<ISpinStateResponse> {
        const response = await this.authService.get(this.resourceHostId,
            `v2/spin/state/user/__userId__?gdc=${this.getGdcType(isWcar)}`);
        const spinResponse = await response.json();

        if (response.status === 403 && spinResponse.type === '/errors/403-3') {
            throw new MissingScopeSpinError();
        }

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return spinResponse;

    }

    public async changeSpin(currentSpin: string, newSpin: string, isWcar: boolean): Promise<void> {
        const spinChangeBody: ISpinChangeBody = {
            existingSPin: currentSpin,
            newSPin: newSpin,
        };
        const response = await this.authService.patch(this.resourceHostId,
            `v2/spin/user/__userId__?gdc=${this.getGdcType(isWcar)}`, JSON.stringify(spinChangeBody),
            { 'Content-Type': 'application/json' });

        if (response.status === 403) {
            throw new InvalidSpinError();
        }

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

    }

    public async resetSpin(newSpin: string, isWcar: boolean): Promise<void> {
        const spinResetBody: ISpinResetBody = { newSPin: newSpin };

        const response = await this.authService.put(this.resourceHostId,
            `v2/spin/user/__userId__?gdc=${this.getGdcType(isWcar)}`, JSON.stringify(spinResetBody),
            { 'Content-Type': 'application/json' });

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

    }

    public async verifySpin(currentSpin: string, isWcar: boolean): Promise<string> {
        const verifySpinBody: ISpinBody = {
            spin: currentSpin,
        };
        const response = await this.authService.post(this.resourceHostId,
            `v2/spin/verify/user/__userId__?gdc=${this.getGdcType(isWcar)}`, JSON.stringify(verifySpinBody),
            { 'Content-Type': 'application/json' });
        const spinResponse = await response.json();

        if (response.status === 403 && spinResponse.type === '/errors/403-4') {
            throw new InvalidSpinError();
        }

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return spinResponse.value;

    }
}
